// Analytics utility wrappers around Google Analytics (gtag) and helpers
// Guards for SSR/headless environments and supports a feature flag via CONFIG

import { CONFIG, getCurrentChannel, API_URL } from '../config';

let engagementIntervalId = null;
let engagedSecondsThisSession = 0;
let lastVisibilityState = typeof document !== 'undefined' ? document.visibilityState : 'visible';

// Barrier to ensure TikTok events wait until identify attempt finishes, without blocking GA/UI
let tiktokIdentifyBarrier = Promise.resolve();
export function setTikTokIdentifyBarrier(promise) {
  try {
    // Always resolve (swallow errors); we only need ordering, not failures propagated
    tiktokIdentifyBarrier = Promise.resolve(promise).catch(() => { });
  } catch (_err) {
    tiktokIdentifyBarrier = Promise.resolve();
  }
}

function getDebugMode() {
  if (!isBrowserEnvironment()) return false;
  try {
    const h = window.location.hostname || '';
    const isLocal = h === 'localhost' || h === '127.0.0.1' || /\.local$/i.test(h);
    const isBetaOrStaging = /beta|staging/i.test(h);
    const fromQuery = /[?&]ga_debug=true/i.test(window.location.search || '');
    const fromStorage = (window.localStorage && localStorage.getItem('ga_debug') === 'true');
    return isLocal || isBetaOrStaging || fromQuery || fromStorage || !!CONFIG?.features?.debug;
  } catch (_err) {
    return !!CONFIG?.features?.debug;
  }
}

function isBrowserEnvironment() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isHeadless() {
  if (!isBrowserEnvironment()) return true;
  try {
    return /HeadlessChrome/.test(navigator.userAgent || '');
  } catch (_err) {
    return false;
  }
}

function isBlockedLocation() {
  if (!isBrowserEnvironment()) return true;
  try {
    // If explicit override is set, never block
    if (CONFIG?.features?.allowBlockedAnalytics) return false;

    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      /\.local$/i.test(hostname) ||
      hostname.includes('192.168.') ||
      hostname.includes('10.0.') ||
      hostname.includes('172.');
    if (isLocalhost) return true;

    // Block Lebanon heuristics
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone === 'Asia/Beirut') return true;
    } catch (_err) { }

    const language = (navigator.language || navigator.languages?.[0] || '').toLowerCase();
    if (language.includes('ar-lb') || language.endsWith('-lb') || language === 'lb') return true;

    return false;
  } catch (_err) {
    return false;
  }
}

function isAnalyticsEnabled() {
  if (!CONFIG?.features?.analytics) return false;
  if (!isBrowserEnvironment()) return false;
  if (isHeadless()) return false;
  if (isBlockedLocation()) return false;
  return typeof window.gtag === 'function' || Array.isArray(window.dataLayer);
}

function isTikTokEnabled() {
  if (!CONFIG?.features?.analytics) return false;
  if (!CONFIG?.features?.tiktok) return false;
  if (!isBrowserEnvironment()) return false;
  if (isHeadless()) return false;
  if (isBlockedLocation()) return false;
  try {
    return !!(window.ttq && typeof window.ttq.track === 'function');
  } catch (_err) {
    return false;
  }
}

function generateEventId() {
  try {
    const rnd = Math.floor(Math.random() * 1e9);
    return `${Date.now()}_${rnd}`;
  } catch (_err) {
    return String(Date.now());
  }
}

export function initAnalytics() {
  if (!isAnalyticsEnabled()) return false;
  return true;
}

export function trackPageView(pagePath, pageTitle) {
  if (!isAnalyticsEnabled()) return;
  const channel = getCurrentChannel();
  const payload = {
    page_path: pagePath || window.location.pathname,
    page_title: pageTitle || document.title,
    page_location: window.location.href,
    channel,
    debug_mode: getDebugMode(),
  };
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', payload);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'page_view', ...payload });
    }
  } catch (_err) {
    // noop
  }
}

export function trackEvent(eventName, params = {}) {
  if (!CONFIG?.features?.analytics) return;
  if (!isBrowserEnvironment() || isHeadless()) return;

  const channel = getCurrentChannel();
  const blocked = isBlockedLocation(); // localhost / LB
  const originalEventName = eventName; // preserve before any mutation
  const payload = { channel, debug_mode: getDebugMode(), ...params };

  // Always send backend relay for "interesting" events even if blocked (for QA and core metrics)
  const interesting = [
    'cta_click',
    'portrait_click',
    'direct_stripe_redirect',
    'stripe_redirect_click',
    'entrance_popup_stripe_redirect',
    'how_it_works_click'
  ];
  try {
    if (interesting.includes(originalEventName)) {
      customEventSender(originalEventName, payload);
    }
  } catch (_err) { /* noop */ }

  // If blocked, skip GA/TikTok only (backend relay already sent above)
  if (blocked) return;

  // Google Analytics / dataLayer
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', originalEventName, payload);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: originalEventName, ...payload });
    }
  } catch (_err) { /* noop */ }

  // TikTok Pixel (send same event name + params)
  try {
    if (isTikTokEnabled()) {
      let ttEventName = originalEventName;
      const ttPayload = { ...payload };
      if (ttEventName === 'submit_click') {
        ttEventName = 'PlaceAnOrder';
        ttPayload.contents = [{ content_id: '1' }];
      }
      Promise.resolve()
        .then(() => tiktokIdentifyBarrier)
        .then(() => {
          if (typeof ttPayload.currency === 'undefined') ttPayload.currency = 'usd';
          if (typeof ttPayload.value === 'undefined') ttPayload.value = 10;
          window.ttq.track(ttEventName, ttPayload, { event_id: ttEventName });
        })
        .catch(() => { /* noop */ });
    }
  } catch (_err) { /* noop */ }
}

// Sends analytics event to backend API (server) /api/analytics endpoint (not frontend)
export async function customEventSender(event, data = {}) {
  if (!isBrowserEnvironment()) return;
  try {
    const body = { event, meta: data };
    if (data.uuid) body.uuid = data.uuid;
    if (data.email) body.email = data.email;
    if (data.value) body.value = data.value;
    // Attempt fetch without blocking UI; no await needed for caller reliability
    const endpoint = `${API_URL}/api/analytics`;
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true, // allow during page unload
    }).catch(() => { /* ignore network errors */ });
  } catch (_err) {
    // noop
  }
}

export function startEngagementTimer() {
  if (!isAnalyticsEnabled()) return;
  if (engagementIntervalId) return; // already started

  const tick = () => {
    if (document.visibilityState === 'visible') {
      engagedSecondsThisSession += 1;
    }
  };

  engagementIntervalId = window.setInterval(tick, 1000);

  const handleVisibilityChange = () => {
    lastVisibilityState = document.visibilityState;
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

export function flushEngagement() {
  if (!isAnalyticsEnabled()) return;
  if (engagedSecondsThisSession <= 0) return;
  try {
    trackEvent('engagement_seconds', {
      value: engagedSecondsThisSession,
    });
  } finally {
    engagedSecondsThisSession = 0;
  }
}

export function attachScrollDepthListener() {
  if (!isAnalyticsEnabled()) return () => { };

  const thresholds = [25, 50, 75, 100];
  const reached = new Set();

  const computeDepthPercent = () => {
    const scrollTop = window.scrollY || window.pageYOffset || 0;
    const docHeight = document.documentElement.scrollHeight || 0;
    const winHeight = window.innerHeight || 0;
    const maxScrollable = Math.max(docHeight - winHeight, 1);
    const percent = Math.min(100, Math.max(0, Math.round((scrollTop / maxScrollable) * 100)));
    return percent;
  };

  const onScroll = () => {
    const depth = computeDepthPercent();
    thresholds.forEach((t) => {
      if (depth >= t && !reached.has(t)) {
        reached.add(t);
        trackEvent('scroll_depth', { percent: t });
      }
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  // Fire once in case the user loads mid-page
  onScroll();

  return function detach() {
    try {
      window.removeEventListener('scroll', onScroll);
    } catch (_err) {
      // noop
    }
  };
}

// --- TikTok Identify helper (PII hashing via SHA-256) ---
let lastTtqIdentityHash = '';

async function sha256Hex(input) {
  if (!input) return '';
  const enc = new TextEncoder();
  const data = enc.encode(String(input).trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(hashBuffer);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    const h = bytes[i].toString(16).padStart(2, '0');
    hex += h;
  }
  return hex;
}

function makeIdentityKey(obj) {
  try {
    return JSON.stringify(obj);
  } catch (_err) {
    return '';
  }
}

/**
 * Identify a user to TikTok pixel with hashed PII. Call once per session/page
 * when appropriate, before sending events that you want attributed.
 * Note: Provide raw values; they will be SHA-256 hashed here.
 */
export async function tiktokIdentify({ email, phone_number, external_id } = {}) {
  if (!CONFIG?.features?.analytics) return;
  if (!isBrowserEnvironment() || isHeadless()) return;
  if (!isTikTokEnabled()) return;

  // Require at least one field
  if (!email && !phone_number && !external_id) return;

  try {
    const hashed = {
      email: email ? await sha256Hex(email) : undefined,
      phone_number: phone_number ? await sha256Hex(phone_number) : undefined,
      external_id: external_id ? await sha256Hex(external_id) : undefined,
    };

    const key = makeIdentityKey(hashed);
    if (key && key === lastTtqIdentityHash) return; // avoid duplicate identify
    lastTtqIdentityHash = key;

    // Only include defined properties
    const toSend = {};
    if (hashed.email) toSend.email = hashed.email;
    if (hashed.phone_number) toSend.phone_number = hashed.phone_number;
    if (hashed.external_id) toSend.external_id = hashed.external_id;

    if (Object.keys(toSend).length > 0) {
      window.ttq.identify(toSend);
    }
  } catch (_err) {
    // noop
  }
}


